import React, { useEffect, useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import {
  Box,
  TextField,
  IconButton,
  Alert,
  InputAdornment,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

import { atom, useSetAtom } from 'jotai';
import SearchIcon from '@mui/icons-material/Search';
import {WritableRestaurantDataSchema } from '../../../client-sdk/api';
import EditRestaurant from './EditRestaurant';
import SelectStore from '../Store/AddStore';
import AddGrocery from '../Store/Grocery';
import AddPharmacy from '../Store/Pharmacy copy';
import EditStoreOwnerModal from '../../stateless/EditModel';
import CommonDataGrid from '../../stateless/CommonDataGrid';
import ErrorHandler from '../../stateless/Common/ErrorHandler';
import { useChoiceParams } from '../ManageChoice/useChoiceParams';
import { useAllStores } from '../../Query/ManageStoreQuery';
import { useDebounce } from '../../../utils/useDounce';
import { useTranslation } from 'react-i18next';

export const storeAtom = atom<any>(null);

type StoreWithSno = Partial<WritableRestaurantDataSchema> & {
  id: string;
  sno: number;
  name?: string;
  merchant?: string;
  merchantEmail?: string;
  category?: string;
  addedBy?: string;
  status?: string;
};

interface ManageRestaurantTableProps {
  storeId?: string;

}

const ManageRestaurantTable: React.FC<ManageRestaurantTableProps> = () => {
  const { t, i18n } = useTranslation();
  const [, forceUpdate] = useState({});
  
  const setStoreAtom = useSetAtom(storeAtom);

  const [storeType, setStoreType] = useState('');

  const [_selectedRows, setSelectedRows] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage] = useState('');
  const [snackbarSeverity] = useState<
    'success' | 'error' | 'info' | 'warning'
  >('info');
  const [modalContent] = useState<'add' | 'edit'>('edit');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);


  const { params, dispatch } = useChoiceParams();

  const isReady = Boolean(params && Object.keys(params).length > 0);

  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);

  const { data: manageStoreData, isLoading, refetch } = useAllStores(memoizedParams);

  const storeType1 = manageStoreData?.data?.map((item) => item.restaurantDetails?.storeType);

  // const { mutate: patchStore } = usePatchStore();

  // const { mutate: deleteStore } = useDeleteStore();

  const filteredRows = React.useMemo(() => {
    const storeOwners = manageStoreData?.data || [];
    const filtered = storeOwners.filter((row) => {
      const searchLower = params.search?.toLowerCase() || '';

      // If no search term provided, return all rows
      if (!searchLower) return true;

      const name = (row.nameEnglish || '').toLowerCase();
      const merchant = (row.merchantName || '').toLowerCase();
      const storeType = (row.restaurantDetails.storeType || '').toLowerCase();

      return (
        name.includes(searchLower) ||
        merchant.includes(searchLower) ||
        storeType.includes(searchLower)
      );
    });

    // Add sno field to each row and ensure all required fields are present
    return filtered.map((row, index) => {
      // Ensure we have a valid row object
      if (!row) return { id: `empty-${index}`, sno: index + 1 } as StoreWithSno;

      // Create a new object with all the row properties plus sno
      const rowWithSno = {
        ...row,
        id: row.id,
        sno: index + 1 + (Number(params.page - 1)) * Number(params.limit),
        name: row.nameEnglish,
        merchant: row.merchantName,
        merchantEmail: row.merchantEmail,
        storeType: row.restaurantDetails.storeType,
        addedBy: row.addedBy,
        status: row.status,
      };

      return {
        ...rowWithSno,
        storeType: rowWithSno.storeType as string,
      } as StoreWithSno;
    });
  }, [params.search, manageStoreData, params.page, params.limit]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch]);

  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };


  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setStoreAtom(null);
  };



  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'sno',
      headerName: t('storeManagement.columns.sno'),
      flex: 0.3,
    },
    {
      field: 'name',
      headerName: t('storeManagement.columns.storeName'),
      flex: 1,
    },
    {
      field: 'merchant',
      headerName: t('storeManagement.columns.merchant'),
      flex: 1.5,
    },
    {
      field: 'merchantEmail',
      headerName: t('storeManagement.columns.email'),
      flex: 1.5,
    },
    {
      field: 'storeType',
      headerName: t('storeManagement.columns.storeType'),
      flex: 1,
    },
    {
      field: 'addedBy',
      headerName: t('storeManagement.columns.addedBy'),
      flex: 1,
    },
    {
      field: 'edit',
      headerName: t('storeManagement.columns.edit'),
      flex: 1,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          onClick={() => {
            setOpenEditDialog(true);
            setStoreAtom(params.row);
            setStoreType(params.row.storeType);
          }}
        >
          <EditIcon />
        </IconButton>
      ),
    },
  ], [t]);

  // Format the manageStoreData for the DataGrid

  return (
    <>
      <Box
        sx={{
          p: 2,
          border: '1px solid #e0e0e0',
          marginTop: '20px',
          color: '#666',
        }}
      >
        {snackbarOpen && (
          <Box sx={{ mb: 2 }}>
            <Alert
              severity={snackbarSeverity}
              onClose={() => setSnackbarOpen(false)}
            >
              {snackbarMessage}
            </Alert>
          </Box>
        )}
        <Box
          sx={{
            // p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2, // Optional: adds spacing between wrapped items
          }}
        >
          {/* Search - Left */}
          <Box
            sx={{
              width: 600,
              border: '1px solid #ccc',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <TextField
              fullWidth
              placeholder={t('storeManagement.searchPlaceholder')}
              size="medium"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                input: {
                  color: '#333', // Visible text color
                  '::placeholder': {
                    color: '#bdbdbd', // Visible placeholder color
                    opacity: 1, // Ensure it doesn't get hidden
                  },
                },
                '.MuiOutlinedInput-root': {
                  height: 40,
                  width: 600,
                  backgroundColor: '#fff', // Make sure input has white background
                },
                '& fieldset': {
                  border: 'none',
                },
                '& .MuiInputBase-input': {
                  paddingTop: '10px', // adjust this value to move the placeholder lower
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 20, color: '#aaa', mt: '4px' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

        </Box>

        <EditStoreOwnerModal
          open={openAddDialog}
          onClose={handleCloseAddDialog}
          title={t('storeManagement.modal.addStore')}
        >
          <SelectStore storeTypeOnEdit={storeType1} onCancel={handleCloseAddDialog} />
        </EditStoreOwnerModal>

        <EditStoreOwnerModal
          open={openEditDialog}
          onClose={handleCloseEditDialog}

          title={modalContent === 'add' ? t('storeManagement.modal.addStore') : t('storeManagement.modal.editStore')}
        >
          {storeType === 'Restaurant' && (
            <EditRestaurant
              onCancel={handleCloseEditDialog}
              storeType={storeType} />
          )}

          {storeType === 'GroceryStore' && (
            <AddGrocery
              onCancel={handleCloseEditDialog}
              storeType={storeType}
              edit="edit"
            />
          )}

          {storeType === 'PharmacyStore' && (
            <AddPharmacy
              onCancel={handleCloseEditDialog}
              storeType={storeType}
              edit="edit"
            />
          )}
        </EditStoreOwnerModal>
        <CommonDataGrid
          key={`datagrid-page-${params.page}-limit-${params.limit}`}
          rows={filteredRows || []}
          columns={columns}
          pageSize={params.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          checkboxSelection
          loading={isLoading}
          page={params.page - 1}
          rowCount={manageStoreData?.pagination?.totalItems || 0}
          components={{
            NoRowsOverlay: () => <ErrorHandler type="no-data" message={t('storeManagement.messages.noData')} onRetry={() => refetch()} />
          }}
          onRowSelectionModelChange={(ids: string[]) => setSelectedRows(ids)}
        />

      </Box>
    </>
  );
};

export default ManageRestaurantTable;

